/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import * as Sentry from '@sentry/react';
import _, { debounce } from 'lodash';

import { Button, Hidden, Typography2, useTheme } from '@coursera/cds-core';
import type { ButtonProps, Theme } from '@coursera/cds-core';
import { CloseIcon } from '@coursera/cds-icons';
import { Select } from '@coursera/coursera-ui';

import A11yScreenReaderOnly from 'bundles/a11y/components/A11yScreenReaderOnly';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { org_coursera_skill_GoalType as GoalType } from 'bundles/program-home/components/enterprise-home/__generated__/globalTypes';
import type {
  PropsFromSkillSetFiltersHOC,
  SelectOption,
} from 'bundles/program-home/components/single-program/SkillSetFiltersHOC';
import LiteracyIcon from 'bundles/program-home/components/single-program/svg/LiteracyIcon';
import ReskillIcon from 'bundles/program-home/components/single-program/svg/ReskillIcon';
import UpskillIcon from 'bundles/program-home/components/single-program/svg/UpskillIcon';

import _t from 'i18n!nls/program-home';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const FILTER_OPTIONS_ANNOUNCEMENT_DELAY = 1500; // ms

type OptionRendererProps = {
  key: string;
  option: SelectOption;
  options: SelectOption[];
  focusOption: (option: SelectOption) => void;
  focusedOption: SelectOption;
  selectValue: (option: SelectOption) => void;
  valueArray: SelectOption[];
  style: React.CSSProperties;
  focusedOptionIndex?: number;
  optionIndex?: number;
};

type PropsFromParent = {
  filterIdPrefix: string;
  showSkillSetRoleFilter: boolean;
};

export type Props = PropsFromParent & Omit<PropsFromSkillSetFiltersHOC, 'isFilterSelected'>;

export const useStyles = () => {
  const theme = useTheme();
  return {
    container: css`
      margin-bottom: ${theme.spacing(24)};
    `,
    heading: css`
      margin: ${theme.spacing(24)} 0 ${theme.spacing(12)} 0;
      text-transform: uppercase;
    `,
    filterControlsContainer: css`
      display: flex;
      flex-flow: row nowrap;
      gap: ${theme.spacing(16)};
      ${theme.breakpoints.down('xs')} {
        gap: ${theme.spacing(8)};
        flex-flow: column nowrap;
      }
    `,
    filterControl: css`
      flex: 1 1 33%;
      min-width: 0;
    `,
    selectLabel: css`
      margin-bottom: ${theme.spacing(8)};
    `,
    select: css`
      .Select-control,
      .Select-menu-outer {
        font-size: 16px;
      }

      .Select-noresults {
        color: #737373;
      }

      .Select-control {
        border-color: ${theme.palette.gray[700]};
        border-radius: 4px;
        height: 48px;
      }

      .Select-placeholder {
        color: ${theme.palette.black[700]};
        font-size: 16px;
        display: flex;
        align-items: center;
      }

      .Select-value {
        display: flex;
        align-items: center;
      }

      .Select-value-label {
        color: ${theme.palette.black[700]} !important;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .Select-arrow {
        border-color: ${theme.palette.gray[700]} transparent transparent; /* Color for minimum A11y contrast against white background https://webaim.org/articles/contrast/#sc143 */
      }

      &.is-open > .Select-control .Select-arrow {
        border-color: transparent transparent ${theme.palette.gray[700]};
      }
    `,
    selectSkills: css`
      /* These styles force the coursera-ui Select control to render multi-select options on one line as a
      comma-separated truncated by an ellipsis. */
      .Select-control {
        align-items: center;
        display: flex;
        gap: 5px;
        position: relative;
      }

      .Select-control,
      .Select-menu-outer {
        font-size: 16px;
      }

      .Select-multi-value-wrapper {
        display: block;
        flex: 1 1 auto;
        overflow: hidden;
        padding: 5px 0 5px 10px;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 16px;

        > * {
          vertical-align: initial;
        }
      }

      .Select-value {
        background: initial;
        border: initial;
        color: ${theme.palette.black[700]} !important;
        display: inline;
        margin: initial;
        padding: initial;
        font-size: 16px;

        &:not(:first-of-type)::before {
          content: ', ';
          display: inline;
        }
      }

      .Select-value-icon {
        display: none;
      }

      .Select-value-label {
        display: inline;
        padding: initial;
        color: inherit;
      }

      .Select-input {
        left: 0;
        margin-left: 0 !important;
        position: absolute;
        top: 0;

        > input {
          padding-left: 10px;
          padding-right: 30px;
        }
      }

      &.has-value.is-focused .Select-input > input:not([value='']) {
        background-image: linear-gradient(to left, rgb(255 255 255 / 0%) 0, rgb(255 255 255 / 95%) 30px);
      }

      .Select-clear-zone,
      .Select-arrow-zone {
        flex: 0 0 auto;
      }
    `,
    selectGoal: css`
      .Select-value {
        margin-right: 32px;
      }
    `,
    disabledSelect: css`
      .Select-control {
        border-color: ${theme.palette.gray[500]};
      }
    `,
    resetButtonContainer: css`
      flex: 0 0 auto;
      align-self: flex-end;
      ${theme.breakpoints.down('xs')} {
        width: 100%;
      }
    `,
    xsResetButton: css`
      width: 100%;
      max-width: 100%;
    `,
    clearIndicator: css`
      width: 12px;
      height: 12px;
      color: ${theme.palette.gray[700]};
    `,
  };
};
const ResetValue = { value: undefined, label: undefined };

export const getGoalLabel = (type: GoalType) => {
  switch (type) {
    case GoalType.LITERACY:
      return _t('Develop foundational awareness in an area of interest');
    case GoalType.UPSKILL:
      return _t('Focus on specific skills to develop skills mastery and advance in your role');
    case GoalType.RESKILL:
      return _t('Learn a broad range of skills to start a new career and grow professionally');
    default:
      return '';
  }
};

export const getFormattedGoalLabel = (type: GoalType) => {
  switch (type) {
    case GoalType.LITERACY:
      return (
        <FormattedMessage
          message={_t('Develop {foundationalAwareness} in an area of interest')}
          foundationalAwareness={<strong>{_t('foundational awareness')}</strong>}
        />
      );
    case GoalType.UPSKILL:
      return (
        <FormattedMessage
          message={_t('Focus on specific skills to develop {skillsMastery} and advance in your role')}
          skillsMastery={<strong>{_t('skills mastery')}</strong>}
        />
      );
    case GoalType.RESKILL:
      return (
        <FormattedMessage
          message={_t('Learn a broad range of skills to {startANewCareer} and grow professionally')}
          startANewCareer={<strong>{_t('start a new career')}</strong>}
        />
      );
    default:
      return '';
  }
};

export const getGoalIcon = (type: GoalType) => {
  switch (type) {
    case GoalType.LITERACY:
      return <LiteracyIcon />;
    case GoalType.UPSKILL:
      return <UpskillIcon />;
    case GoalType.RESKILL:
      return <ReskillIcon />;
    default:
      return '';
  }
};

const getSelectMessages = (type = '', input = '') => ({
  backspaceToRemoveMessage: _t('Press backspace to remove {label}'),
  noResultsText: (
    <div>
      {_t('No results found')}
      <A11yScreenReaderOnly tagName="span">
        {type !== '' && input !== '' ? (
          <span role="alert">{_t('No #{type} filter option found for input #{input}', { type, input })}</span>
        ) : (
          <span role="alert">{_t('No filter option found')}</span>
        )}
      </A11yScreenReaderOnly>
    </div>
  ),
  clearValueText: _t('Clear #{type} filter', { type }),
  clearAllText: _t('Clear all #{type} filter', { type }),
});

const optionLocaleCompare = (a: SelectOption, b: SelectOption): number => a.label.localeCompare(b.label);

type AnnounceFilterOptionsProps = {
  filterOptionsCount: number;
};
const AnnounceFilterOptions = ({ filterOptionsCount }: AnnounceFilterOptionsProps) => {
  const [optionsCount, setOptionsCount] = useState(0);
  const optionsCountNotificationRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (optionsCountNotificationRef.current) {
      optionsCountNotificationRef.current.style.display = 'block';
    }

    // Announcing the new option count is delayed because the screen reader announces
    // each input character then attempts to pronounce the word typed, which gets in the way of this notification.
    const updateOptionsCount = debounce(() => {
      setOptionsCount(filterOptionsCount);
      setTimeout(() => {
        if (optionsCountNotificationRef.current) {
          optionsCountNotificationRef.current.style.display = 'none';
        }
      }, FILTER_OPTIONS_ANNOUNCEMENT_DELAY / 2);
    }, FILTER_OPTIONS_ANNOUNCEMENT_DELAY);

    updateOptionsCount();

    // To clean up the debounce timeout on unmount
    return () => {
      updateOptionsCount.cancel();
    };
  }, [filterOptionsCount]);

  return (
    <A11yScreenReaderOnly tagName="span">
      <span ref={optionsCountNotificationRef} aria-live="polite">
        {optionsCount > 0 ? _t('#{optionsCount} filter options displayed', { optionsCount }) : ''}
      </span>
    </A11yScreenReaderOnly>
  );
};

type GoalOptionRendererProps = OptionRendererProps & {
  filterIdPrefix: string;
};
const goalOptionRenderer = (
  {
    key,
    option,
    options,
    focusedOption,
    focusOption,
    selectValue,
    valueArray,
    style,
    optionIndex,
    filterIdPrefix,
  }: GoalOptionRendererProps,
  theme: Theme
) => {
  const isSelectedOption = valueArray.some(({ value }: SelectOption) => value === option.value);
  // can't use the useStyles hook in here because this is not a component
  const styles = {
    optionContainer: css`
      display: flex;
      align-items: center;
      gap: ${theme.spacing(12)};
      padding: ${theme.spacing(16)};
    `,
    label: css`
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    `,
    selectedOption: css`
      font-weight: bold;
    `,
  };

  return (
    // disabled some a11y rules because the cui Select component isn't accessible :(
    // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events, jsx-a11y/click-events-have-key-events,
    <div
      role="option"
      id={`react-select-${filterIdPrefix}--option-${optionIndex}`}
      onClick={() => selectValue(option)}
      onMouseOver={() => focusOption(option)}
      style={style}
      key={key}
      tabIndex={-1} // Options in popups should not be in tab sequence https://www.w3.org/WAI/ARIA/apg/patterns/combobox/
      aria-selected={isSelectedOption}
      css={[styles.optionContainer, isSelectedOption && styles.selectedOption]}
      className={option.value === focusedOption.value ? 'VirtualizedSelectFocusedOption' : undefined}
    >
      {/* the icon is hidden for small screen sizes so that it's easier to read the label when the Select box is a small width */}
      <Hidden only="sm">{getGoalIcon(option.value as GoalType)}</Hidden>
      {/* Inserting this on the first option is enough to get the screen reader announce it */}
      {optionIndex === 0 && <AnnounceFilterOptions filterOptionsCount={options.length}></AnnounceFilterOptions>}
      <div css={styles.label}>{getFormattedGoalLabel(option.value as GoalType)}</div>
    </div>
  );
};

type SelectAccessibleOptionRendererProps = OptionRendererProps & {
  filterIdPrefix: string;
};

// We use to override the default option renderer of our selects. We do this to improve accessibility and ensure the screenreader can read the options.
// See: https://coursera.atlassian.net/browse/ACCESS-2498
const SelectAccessibleOptionRenderer = ({
  key,
  option,
  options,
  focusOption,
  focusedOptionIndex,
  selectValue,
  style,
  optionIndex,
  filterIdPrefix,
  valueArray,
}: SelectAccessibleOptionRendererProps) => {
  const theme = useTheme();
  const isSelected = valueArray.some(({ value }: SelectOption) => value === option.value);
  const isFocused = focusedOptionIndex === optionIndex;
  return (
    // tabIndex={-1} breaks the menu in firefox
    // eslint-disable-next-line jsx-a11y/interactive-supports-focus
    <div
      data-testid="SelectAccessibleOptionRenderer"
      key={key}
      role="option"
      id={`react-select-${filterIdPrefix}--option-${optionIndex}`}
      aria-selected={isSelected}
      onClick={() => selectValue(option)}
      onMouseOver={() => focusOption(option)}
      data-e2e="virtualized-select-option-custom"
      onFocus={() => {
        // noop
        // Handled by combobox
      }}
      onKeyPress={() => {
        // noop
        // Handled by combobox
      }}
      style={style}
      css={css({
        display: 'flex',
        alignContent: 'center',
        backgroundColor: isFocused ? theme.palette.blue[100] : 'white',
        '&:hover': {
          backgroundColor: theme.palette.blue[100],
        },
        padding: theme.spacing(8, 8),
        color: theme.palette.black[700],
      })}
    >
      {optionIndex === 0 && <AnnounceFilterOptions filterOptionsCount={options.length}></AnnounceFilterOptions>}
      {option.label}
    </div>
  );
};

type SelectCloseIndicatorProps = {
  onEnterPress: () => void;
};
const SelectCloseIndicator = ({ onEnterPress }: SelectCloseIndicatorProps) => {
  const styles = useStyles();

  return (
    <CloseIcon
      tabIndex={0}
      aria-label={_t('Dismiss all')}
      role="button"
      data-testid="dismiss-button"
      css={styles.clearIndicator}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onEnterPress();
          event.stopPropagation();
        } else if (event.key === 'ArrowDown') {
          event.stopPropagation();
          // Down arrows opens the combobox. We do not want this to happen whilst the dismiss button is focused
        }
      }}
    />
  );
};

// this component can probably be simplified/refactored
type PropsForPlaceholder = {
  showSkillSetRoleFilter: boolean;
};
export const SkillSetFiltersPlaceholder = ({ showSkillSetRoleFilter }: PropsForPlaceholder) => {
  const styles = useStyles();
  const selectMessages = getSelectMessages();

  return (
    <div css={styles.container}>
      <Typography2 variant="subtitleMedium" component="div" color="supportText" css={styles.heading}>
        {_t('Filter SkillSets by:')}
      </Typography2>
      <div css={styles.filterControlsContainer}>
        <div css={styles.filterControl}>
          <Typography2 htmlFor="skillsFilter" component="label" css={styles.selectLabel}>
            {_t('Skills:')}
          </Typography2>
          <Select
            disabled
            aria-busy
            aria-label={_t('Loading...')}
            multi
            searchable
            clearable
            resetValue={ResetValue}
            id="skillsFilter"
            placeholder={_t('Select skills...')}
            css={[styles.select, styles.disabledSelect]}
            onChange={_.noop}
            {...selectMessages}
          />
        </div>
        {showSkillSetRoleFilter && (
          <div css={styles.filterControl}>
            <Typography2 htmlFor="roleFilter" component="label" css={styles.selectLabel}>
              {_t('Role:')}
            </Typography2>
            <Select
              disabled
              aria-busy
              aria-label={_t('Loading...')}
              searchable
              clearable
              id="roleFilter"
              placeholder={_t('Select a role...')}
              css={[styles.select, styles.selectSkills, styles.disabledSelect]}
              onChange={_.noop}
              {...selectMessages}
            />
          </div>
        )}
        <div css={styles.filterControl}>
          <Typography2 htmlFor="goalFilter" component="label" css={styles.selectLabel}>
            {_t('Goal:')}
          </Typography2>
          <Select
            disabled
            aria-busy
            aria-label={_t('Loading...')}
            searchable
            clearable
            resetValue={ResetValue}
            id="goalFilter"
            placeholder={_t('Select a goal...')}
            css={[styles.select, styles.disabledSelect]}
            onChange={_.noop}
            {...selectMessages}
          />
        </div>
        <div css={styles.resetButtonContainer}>
          <Hidden xsDown>
            <Button disabled variant="ghost">
              {_t('Reset')}
            </Button>
          </Hidden>
          <Hidden smUp>
            <Button disabled variant="secondary" size="small" css={styles.xsResetButton}>
              {_t('Reset Filters')}
            </Button>
          </Hidden>
        </div>
      </div>
    </div>
  );
};

export const SkillSetFiltersContainer: React.FC<Props> = ({
  filterOptions,
  allOccupations,
  skillNames,
  filterSelections,
  onRoleChange,
  onSkillsChange,
  onGoalChange,
  onFilterReset,
  onFilterInputChange,
  showSkillSetRoleFilter,
  filterIdPrefix,
}) => {
  const styles = useStyles();
  const theme = useTheme();
  const [filterInFocus, setFilterInFocus] = useState('');
  const [inputOfFilterInFocus, setInputOfFilterInFocus] = useState('');

  // UseLayoutEffect is run synchronously after React DOM mutations and before painting which makes it good for DOM manipulations: https://kentcdodds.com/blog/useeffect-vs-uselayouteffect
  useLayoutEffect(() => {
    try {
      /*
       *This div gets read as a group when using voice over in chrome.
       *Setting this div to role none fixes it but there is no api to access the div so we manipulate it manually.
       */
      document.querySelectorAll('.rc-SkillSetFiltersContainer .Select-multi-value-wrapper').forEach((node) => {
        node.setAttribute('role', 'none');
      });

      // Setting this span to role button to prevent screen reader from announcing the dismiss button as group or combobox
      document.querySelectorAll('.rc-SkillSetFiltersContainer .Select-clear-zone').forEach((node) => {
        node.setAttribute('role', 'button');
      });
    } catch (error) {
      Sentry.captureException('Error whilst manipulating select multi value wrapper role.', { extra: { error } });
    }
  });

  if (
    !filterOptions ||
    !allOccupations ||
    _.isEmpty(allOccupations) ||
    !skillNames ||
    _.isEmpty(skillNames) ||
    !filterSelections
  ) {
    return <SkillSetFiltersPlaceholder showSkillSetRoleFilter={showSkillSetRoleFilter} />;
  }

  const roleFilterOptions = [...filterOptions.roles]
    .map((role) => ({
      value: role,
      label: allOccupations[role] || '',
    }))
    .filter(({ label }) => !!label)
    .sort(optionLocaleCompare);
  const skillsFilterOptions = [...filterOptions.skills]
    .map((skill) => ({
      value: skill,
      label: skillNames[skill] || '',
    }))
    .filter(({ label }) => !!label)
    .sort(optionLocaleCompare);
  const goalFilterOptions = [...filterOptions.goals]
    .map((goal) => ({
      value: goal,
      label: getGoalLabel(goal),
    }))
    .filter(({ label }) => !!label)
    .sort(optionLocaleCompare);

  const onInputChange = (e: string, type: 'role' | 'skill' | 'goal') => {
    setTimeout(() => {
      setFilterInFocus(type);
      setInputOfFilterInFocus(e);
    }, FILTER_OPTIONS_ANNOUNCEMENT_DELAY);
    onFilterInputChange?.(e, type);
  };

  const skillsFilterId = filterIdPrefix + 'skillsFilter';
  const rolesFilterId = filterIdPrefix + 'roleFilter';
  const goalFilterId = filterIdPrefix + 'goalFilter';
  return (
    <TrackedDiv
      role="group"
      aria-label={_t('SkillSet Filters')}
      trackingName="skillset_filters"
      data-e2e="program-home-skillset-filters-container"
      withVisibilityTracking={true}
      trackClicks={false}
      css={styles.container}
      className="rc-SkillSetFiltersContainer"
    >
      <Typography2 variant="subtitleMedium" component="div" color="supportText" css={styles.heading}>
        {_t('Filter SkillSets by:')}
      </Typography2>
      <div css={styles.filterControlsContainer}>
        <div css={styles.filterControl}>
          <Typography2 htmlFor={skillsFilterId} variant="subtitleMedium" component="label" css={styles.selectLabel}>
            {_t('Skills:')}
          </Typography2>
          <Select
            multi
            searchable
            clearable
            htmlAttributes={{ removeSelected: false, 'aria-label': _t('Skills') }}
            placeholder={<span aria-hidden>{_t('Select skills...')}</span>} // The aria-hidden prevents the placeholder being announced instead of the label in chrome
            id={skillsFilterId}
            value={filterSelections.skills}
            options={skillsFilterOptions}
            css={[styles.select, styles.selectSkills]}
            onChange={onSkillsChange}
            onInputChange={(e: string) => onInputChange(e, 'skill')}
            optionRenderer={(props: OptionRendererProps) => (
              <SelectAccessibleOptionRenderer {...props} filterIdPrefix={skillsFilterId} />
            )}
            listProps={{
              role: 'listbox', // Needed to prevent screen reader thinking its a grid
            }}
            {...getSelectMessages(_t('Skills'), inputOfFilterInFocus)}
            clearRenderer={() => (
              <SelectCloseIndicator
                onEnterPress={() => {
                  onSkillsChange?.([]);
                  document.getElementById(skillsFilterId)?.focus();
                }}
              />
            )}
          />
        </div>
        {showSkillSetRoleFilter && (
          <div css={styles.filterControl}>
            <Typography2 htmlFor={rolesFilterId} variant="subtitleMedium" component="label" css={styles.selectLabel}>
              {_t('Role:')}
            </Typography2>
            <Select
              searchable
              clearable
              resetValue={ResetValue}
              htmlAttributes={{ removeSelected: false, 'aria-label': _t('Role') }}
              placeholder={<span aria-hidden>{_t('Select a role...')}</span>} // The aria-hidden prevents the placeholder being announced instead of the label in chrome
              id={rolesFilterId}
              value={filterSelections.role}
              options={roleFilterOptions}
              css={styles.select}
              onChange={onRoleChange}
              onInputChange={(e: string) => onInputChange(e, 'role')}
              optionRenderer={(props: OptionRendererProps) => (
                <SelectAccessibleOptionRenderer {...props} filterIdPrefix={rolesFilterId} />
              )}
              listProps={{
                role: 'listbox', // Needed to prevent screen reader thinking its a grid
              }}
              {...getSelectMessages(_t('Role'), inputOfFilterInFocus)}
              clearRenderer={() => (
                <SelectCloseIndicator
                  onEnterPress={() => {
                    onRoleChange?.({ value: '', label: '' });
                    document.getElementById(rolesFilterId)?.focus();
                  }}
                />
              )}
            />
          </div>
        )}
        <div css={styles.filterControl}>
          <Typography2 htmlFor={goalFilterId} variant="subtitleMedium" component="label" css={styles.selectLabel}>
            {_t('Goal:')}
          </Typography2>
          <Select
            searchable
            clearable
            resetValue={ResetValue}
            htmlAttributes={{ removeSelected: false, 'aria-label': _t('Goal') }}
            placeholder={<span aria-hidden>{_t('Select a goal...')}</span>} // The aria-hidden prevents the placeholder being announced instead of the label in chrome
            id={goalFilterId}
            value={filterSelections.goal}
            options={goalFilterOptions}
            css={[styles.select, styles.selectGoal]}
            optionHeight={75}
            optionRenderer={(props: OptionRendererProps) =>
              goalOptionRenderer({ ...props, filterIdPrefix: goalFilterId }, theme)
            }
            onChange={onGoalChange}
            onInputChange={(e: string) => onInputChange(e, 'goal')}
            listProps={{
              role: 'listbox', // Needed to prevent screen reader thinking its a grid
            }}
            {...getSelectMessages(_t('Goal'), inputOfFilterInFocus)}
            clearRenderer={() => (
              <SelectCloseIndicator
                onEnterPress={() => {
                  onGoalChange?.({ value: '', label: '' });
                  document.getElementById(goalFilterId)?.focus();
                }}
              />
            )}
          />
        </div>
        <div css={styles.resetButtonContainer}>
          <Hidden xsDown>
            <TrackedButton
              {...Button.defaultProps}
              trackingName="skillset_filters_reset_button"
              variant="ghost"
              onClick={onFilterReset}
            >
              {_t('Reset')}
            </TrackedButton>
          </Hidden>
          <Hidden smUp>
            <TrackedButton
              {...Button.defaultProps}
              trackingName="skillset_filters_reset_button"
              variant="secondary"
              size="small"
              css={styles.xsResetButton}
              onClick={onFilterReset}
            >
              {_t('Reset Filters')}
            </TrackedButton>
          </Hidden>
        </div>
      </div>
    </TrackedDiv>
  );
};
